import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";

export default function Navbar() {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const nav = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const linkCls = ({ isActive }) =>
    `text-sm tracking-wide transition-colors duration-300 ${
      isActive ? "text-[#D4AF37]" : "text-[#F5F2EB]/80 hover:text-[#D4AF37]"
    }`;

  return (
    <header
      data-testid="main-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled ? "glass border-b border-white/5" : "bg-transparent"
      }`}
    >
      <nav className="max-w-[1400px] mx-auto px-6 md:px-12 h-20 flex items-center justify-between">
        <Link to="/" data-testid="nav-logo" className="flex items-baseline gap-2 group">
          <span className="font-display text-2xl tracking-tight text-[#F5F2EB] group-hover:text-[#D4AF37] transition-colors">
            Marilyn Dale
          </span>
          <span className="hidden sm:inline text-[10px] tracking-[0.3em] uppercase text-[#B3AFA8]">Silk and Slip · Making Pottery</span>
        </Link>

        <div className="hidden md:flex items-center gap-10">
          <NavLink to="/classes" className={linkCls} data-testid="nav-classes">Classes</NavLink>
          <NavLink to="/chat" className={linkCls} data-testid="nav-chat">Ask Marilyn</NavLink>
          {user && <NavLink to="/dashboard" className={linkCls} data-testid="nav-dashboard">Dashboard</NavLink>}
          {user ? (
            <div className="flex items-center gap-4">
              <span className="text-xs tracking-wide text-[#B3AFA8]">Hi, {user.name.split(" ")[0]}</span>
              <button
                data-testid="nav-logout"
                onClick={() => { logout(); nav("/"); }}
                className="btn-outline-gold text-xs px-4 py-2 rounded-full"
              >
                Sign out
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-3">
              <Link to="/login" data-testid="nav-login" className="text-sm text-[#F5F2EB]/80 hover:text-[#D4AF37] transition">
                Log in
              </Link>
              <Link to="/signup" data-testid="nav-signup" className="btn-gold text-xs px-5 py-2.5 rounded-full">
                Join the Studio
              </Link>
            </div>
          )}
        </div>

        <button
          data-testid="nav-mobile-toggle"
          onClick={() => setOpen(!open)}
          className="md:hidden text-[#F5F2EB]"
          aria-label="Toggle menu"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </nav>

      {open && (
        <div className="md:hidden glass border-t border-white/5 px-6 py-6 space-y-4">
          <NavLink to="/classes" onClick={() => setOpen(false)} className="block text-[#F5F2EB] hover:text-[#D4AF37]">Classes</NavLink>
          <NavLink to="/chat" onClick={() => setOpen(false)} className="block text-[#F5F2EB] hover:text-[#D4AF37]">Ask Marilyn</NavLink>
          {user && <NavLink to="/dashboard" onClick={() => setOpen(false)} className="block text-[#F5F2EB] hover:text-[#D4AF37]">Dashboard</NavLink>}
          {user ? (
            <button onClick={() => { logout(); setOpen(false); nav("/"); }} className="btn-outline-gold w-full px-4 py-2 rounded-full">Sign out</button>
          ) : (
            <div className="flex flex-col gap-3">
              <Link to="/login" onClick={() => setOpen(false)} className="text-[#F5F2EB]">Log in</Link>
              <Link to="/signup" onClick={() => setOpen(false)} className="btn-gold text-center px-5 py-2.5 rounded-full">Join the Studio</Link>
            </div>
          )}
        </div>
      )}
    </header>
  );
}
